import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ErrorPanel } from '../components/ui/ErrorPanel';
import { createCase, type CreateCaseInput } from '../lib/supabaseCases';

type CaseType = 'b2c' | 'b2g';
type LegalBasis = 'par10' | 'par16b' | 'par16c' | 'par93';

interface FormState {
  type: CaseType;
  legalBasis: LegalBasis;
  // Mandant
  clientFullName: string;
  clientBirthdate: string;
  clientAddress: string;
  // Geschäftsdaten
  businessBranch: string;
  businessLegalForm: string;
  businessStartDate: string;
  businessTaxId: string;
  businessDescription: string;
  // Jobcenter (B2G)
  jobcenterName: string;
  jobcenterAddressLine1: string;
  jobcenterAddressLine2: string;
  jobcenterContactName: string;
  jobcenterFileNumber: string;
  orderDate: string;
  // Termin
  benefitStartDate: string;
  dataSubmittedAt: string;
  meetingDate: string;
  meetingDurationMin: string;
  meetingFormat: '' | 'vor_ort' | 'video' | 'telefon';
  additionalDocs: string;
}

const INITIAL: FormState = {
  type: 'b2g',
  legalBasis: 'par10',
  clientFullName: '',
  clientBirthdate: '',
  clientAddress: '',
  businessBranch: '',
  businessLegalForm: '',
  businessStartDate: '',
  businessTaxId: '',
  businessDescription: '',
  jobcenterName: '',
  jobcenterAddressLine1: '',
  jobcenterAddressLine2: '',
  jobcenterContactName: '',
  jobcenterFileNumber: '',
  orderDate: '',
  benefitStartDate: '',
  dataSubmittedAt: '',
  meetingDate: '',
  meetingDurationMin: '',
  meetingFormat: '',
  additionalDocs: '',
};

const LEGAL_BASIS_OPTIONS: ReadonlyArray<{ value: LegalBasis; label: string; hint: string }> = [
  {
    value: 'par10',
    label: '§ 10 Abs. 2 Nr. 5 SGB II',
    hint: 'Tragfähigkeitsprüfung nach 1 Jahr Bürgergeld-Bezug',
  },
  { value: 'par16b', label: '§ 16b SGB II', hint: 'Einstiegsgeld' },
  {
    value: 'par16c',
    label: '§ 16c SGB II',
    hint: 'Förderung Selbständigkeit (Sachmittel, Beratung)',
  },
  { value: 'par93', label: '§ 93 SGB III', hint: 'Gründungszuschuss (Vorab-Tragfähigkeit)' },
];

export function NewCase() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  function trim(s: string): string | undefined {
    const t = s.trim();
    return t.length > 0 ? t : undefined;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload: CreateCaseInput = {
        type: form.type,
        legalBasis: form.legalBasis,
      };
      const ass = (k: keyof CreateCaseInput, v: string | undefined) => {
        if (v !== undefined) (payload as unknown as Record<string, unknown>)[k] = v;
      };
      ass('clientFullName', trim(form.clientFullName));
      ass('clientBirthdate', trim(form.clientBirthdate));
      ass('clientAddress', trim(form.clientAddress));
      ass('businessBranch', trim(form.businessBranch));
      ass('businessLegalForm', trim(form.businessLegalForm));
      ass('businessStartDate', trim(form.businessStartDate));
      ass('businessTaxId', trim(form.businessTaxId));
      ass('businessDescription', trim(form.businessDescription));
      if (form.type === 'b2g') {
        ass('jobcenterName', trim(form.jobcenterName));
        ass('jobcenterAddressLine1', trim(form.jobcenterAddressLine1));
        ass('jobcenterAddressLine2', trim(form.jobcenterAddressLine2));
        ass('jobcenterContactName', trim(form.jobcenterContactName));
        ass('jobcenterFileNumber', trim(form.jobcenterFileNumber));
        ass('orderDate', trim(form.orderDate));
      }
      ass('benefitStartDate', trim(form.benefitStartDate));
      ass('dataSubmittedAt', trim(form.dataSubmittedAt));
      ass('meetingDate', trim(form.meetingDate));
      ass('additionalDocs', trim(form.additionalDocs));
      const durationRaw = trim(form.meetingDurationMin);
      if (durationRaw) {
        const n = Number(durationRaw);
        if (Number.isFinite(n) && n > 0) {
          (payload as unknown as Record<string, unknown>).meetingDurationMin = Math.round(n);
        }
      }
      if (form.meetingFormat) {
        (payload as unknown as Record<string, unknown>).meetingFormat = form.meetingFormat;
      }

      const created = await createCase(payload);
      void navigate(`/case/${created.id}/hub`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Anlegen.');
    } finally {
      setSubmitting(false);
    }
  }

  const showJobcenter = form.type === 'b2g';

  return (
    <div className="pb-24 pt-12 md:pt-20">
      <button
        type="button"
        onClick={() => {
          void navigate('/cases');
        }}
        className="mb-6 inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft size={14} />
        Mandate
      </button>

      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-private)', opacity: 0.32 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Neues Mandat
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          MANDAT ANLEGEN.
        </h1>
        <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
          Stammdaten und Förderkontext erfassen. Sensible Daten (BWA, Bedarfsgemeinschaft,
          Gesundheit) werden später im Wizard ergänzt.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-12"
      >
        {/* Förderkontext */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Förderkontext"
            title="ART DES MANDATS"
            description="B2G = Auftrag durch Jobcenter (formelle Stellungnahme). B2C = Mandant beauftragt Petschwork direkt (Vorbereitung)."
          />
          <RadioGroup
            label="Mandatsart"
            options={[
              { value: 'b2g', label: 'B2G — Jobcenter-Auftrag', hint: 'Formelle Stellungnahme' },
              {
                value: 'b2c',
                label: 'B2C — Mandanten-Vorbereitung',
                hint: 'Beratung, AVGS-finanzierbar',
              },
            ]}
            value={form.type}
            onChange={(v) => {
              patch('type', v as CaseType);
            }}
          />
          <RadioGroup
            label="Rechtsgrundlage"
            options={LEGAL_BASIS_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
              hint: o.hint,
            }))}
            value={form.legalBasis}
            onChange={(v) => {
              patch('legalBasis', v as LegalBasis);
            }}
          />
        </section>

        {/* Mandanten-Stammdaten */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Mandant"
            title="STAMMDATEN"
            description="Datenminimierung: Geburtsdatum nur, wenn Altersgrenzen relevant; Adresse nur, wenn für die Stellungnahme nötig."
          />
          <TextField
            label="Vollständiger Name"
            value={form.clientFullName}
            onChange={(v) => {
              patch('clientFullName', v);
            }}
            placeholder="z.B. Maria Mustermann"
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <DateField
              label="Geburtsdatum"
              hint="optional, nur wenn relevant"
              value={form.clientBirthdate}
              onChange={(v) => {
                patch('clientBirthdate', v);
              }}
            />
            <TextField
              label="Adresse"
              hint="einzeilig oder mehrzeilig"
              value={form.clientAddress}
              onChange={(v) => {
                patch('clientAddress', v);
              }}
              multiline
              placeholder="z.B. Hauptstr. 12, 80331 München"
            />
          </div>
        </section>

        {/* Geschäftsdaten */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Geschäftsdaten"
            title="GESCHÄFTSDATEN"
            description="Stammdaten zum geprüften Unternehmen. Werden in der Stellungnahme zitiert. Felder können später im Cockpit ergänzt werden."
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <TextField
              label="Branche / Gewerbe"
              value={form.businessBranch}
              onChange={(v) => {
                patch('businessBranch', v);
              }}
              placeholder="z.B. Beratung und Coaching"
            />
            <TextField
              label="Rechtsform"
              value={form.businessLegalForm}
              onChange={(v) => {
                patch('businessLegalForm', v);
              }}
              placeholder="z.B. Einzelunternehmen, GbR, UG"
            />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <DateField
              label="Beginn selbständige Tätigkeit"
              value={form.businessStartDate}
              onChange={(v) => {
                patch('businessStartDate', v);
              }}
            />
            <TextField
              label="Steuernummer"
              hint="NICHT die persönliche Steuer-ID — die Steuernummer des Betriebs"
              value={form.businessTaxId}
              onChange={(v) => {
                patch('businessTaxId', v);
              }}
              placeholder="z.B. 143/123/45678"
            />
          </div>
          <TextField
            label="Kurzbeschreibung der Tätigkeit"
            value={form.businessDescription}
            onChange={(v) => {
              patch('businessDescription', v);
            }}
            multiline
            placeholder="z.B. Mediation und Konfliktberatung für KMU"
          />
        </section>

        {/* Jobcenter — nur bei B2G */}
        {showJobcenter && (
          <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
            <SectionHeader
              eyebrow="Auftraggeber (B2G)"
              title="JOBCENTER"
              description="Jobcenter-Name und Aktenzeichen sind Pflichtfelder bei B2G-Mandaten."
            />
            <TextField
              label="Jobcenter-Name ★"
              value={form.jobcenterName}
              onChange={(v) => {
                patch('jobcenterName', v);
              }}
              placeholder="z.B. Jobcenter München Stadt"
              required
            />
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <TextField
                label="Aktenzeichen ★"
                value={form.jobcenterFileNumber}
                onChange={(v) => {
                  patch('jobcenterFileNumber', v);
                }}
                placeholder="z.B. IFK-2026-12345"
                required
              />
              <DateField
                label="Auftragsdatum"
                value={form.orderDate}
                onChange={(v) => {
                  patch('orderDate', v);
                }}
              />
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <TextField
                label="Adresszeile 1"
                value={form.jobcenterAddressLine1}
                onChange={(v) => {
                  patch('jobcenterAddressLine1', v);
                }}
                placeholder="Straße + Nr."
              />
              <TextField
                label="Adresszeile 2"
                value={form.jobcenterAddressLine2}
                onChange={(v) => {
                  patch('jobcenterAddressLine2', v);
                }}
                placeholder="PLZ + Ort"
              />
            </div>
            <TextField
              label="Ansprechpartner"
              value={form.jobcenterContactName}
              onChange={(v) => {
                patch('jobcenterContactName', v);
              }}
              placeholder="z.B. Frau Schneider"
            />
          </section>
        )}

        {/* Verfahren */}
        <section className="space-y-6 rounded-sm border border-stone-200 bg-white p-6 md:p-8">
          <SectionHeader
            eyebrow="Verfahren"
            title="ZEITLICHE EINORDNUNG"
            description="Optional, kann im Cockpit nachgepflegt werden."
          />
          <DateField
            label="Beginn Leistungsbezug"
            hint="für § 10-Tragfähigkeitsprüfung relevant"
            value={form.benefitStartDate}
            onChange={(v) => {
              patch('benefitStartDate', v);
            }}
          />
          <DateField
            label="Datum der Datenübergabe"
            hint="wann die Unterlagen eingereicht wurden"
            value={form.dataSubmittedAt}
            onChange={(v) => {
              patch('dataSubmittedAt', v);
            }}
          />
          <DateField
            label="Datum des Beratungsgesprächs"
            value={form.meetingDate}
            onChange={(v) => {
              patch('meetingDate', v);
            }}
          />
          <TextField
            label="Dauer des Beratungsgesprächs (Minuten)"
            value={form.meetingDurationMin}
            onChange={(v) => {
              patch('meetingDurationMin', v.replace(/[^0-9]/g, ''));
            }}
            placeholder="z.B. 60"
          />
          <RadioGroup
            label="Format des Beratungsgesprächs"
            options={[
              { value: 'vor_ort', label: 'Präsenztermin' },
              { value: 'video', label: 'Videotermin' },
              { value: 'telefon', label: 'Telefonisch' },
            ]}
            value={form.meetingFormat}
            onChange={(v) => {
              patch('meetingFormat', v as FormState['meetingFormat']);
            }}
          />
          <TextField
            label="Zusätzliche Unterlagen"
            value={form.additionalDocs}
            onChange={(v) => {
              patch('additionalDocs', v);
            }}
            multiline
            placeholder="z.B. Auftragsbestand Q2 2026, Liquiditätsplan"
          />
        </section>

        {/* Submit */}
        <section className="flex flex-col items-start gap-4 border-t border-stone-200 pt-8 md:flex-row md:items-center md:justify-between">
          <div className="font-serif text-sm italic text-stone-500">
            Sie können später jederzeit Stammdaten ergänzen oder ändern.
          </div>
          <div className="flex flex-col items-end gap-3">
            {error && (
              <div className="w-full max-w-md">
                <ErrorPanel messages={[error]} />
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-sm bg-stone-900 px-6 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-400"
            >
              {submitting ? 'Lege an…' : 'Mandat anlegen →'}
            </button>
          </div>
        </section>
      </form>
    </div>
  );
}

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  description: string;
}

function SectionHeader({ eyebrow, title, description }: SectionHeaderProps) {
  return (
    <div>
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {eyebrow}
      </p>
      <h2 className="mt-1 font-serif text-xl font-bold uppercase tracking-tight text-stone-900">
        {title}
      </h2>
      <p className="mt-1 max-w-prose font-serif text-sm italic text-stone-500">{description}</p>
    </div>
  );
}

interface TextFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  required?: boolean;
}

function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  multiline,
  required,
}: TextFieldProps) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
      >
        {label}
      </label>
      {hint && <p className="mt-1 font-serif text-xs italic text-stone-500">{hint}</p>}
      {multiline ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          rows={3}
          required={required}
          className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
        />
      ) : (
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder={placeholder}
          required={required}
          className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
        />
      )}
    </div>
  );
}

interface DateFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}

function DateField({ label, hint, value, onChange }: DateFieldProps) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700"
      >
        {label}
      </label>
      {hint && <p className="mt-1 font-serif text-xs italic text-stone-500">{hint}</p>}
      <input
        id={id}
        type="date"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="mt-1 w-full rounded-sm border border-stone-200 bg-white px-3 py-2 font-serif text-base text-stone-900 focus:border-stone-900 focus:outline-none focus:ring-1 focus:ring-stone-900"
      />
    </div>
  );
}

interface RadioOption {
  value: string;
  label: string;
  hint?: string;
}

interface RadioGroupProps {
  label: string;
  options: ReadonlyArray<RadioOption>;
  value: string;
  onChange: (v: string) => void;
}

function RadioGroup({ label, options, value, onChange }: RadioGroupProps) {
  const groupId = useId();
  return (
    <fieldset>
      <legend className="font-sans text-xs font-medium uppercase tracking-widest text-stone-700">
        {label}
      </legend>
      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        {options.map((opt) => {
          const id = `${groupId}-${opt.value}`;
          const checked = opt.value === value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={`cursor-pointer rounded-sm border px-4 py-3 transition-colors ${
                checked
                  ? 'border-stone-900 bg-stone-900 text-white'
                  : 'border-stone-200 bg-white text-stone-900 hover:border-stone-400'
              }`}
            >
              <input
                id={id}
                type="radio"
                name={groupId}
                value={opt.value}
                checked={checked}
                onChange={() => {
                  onChange(opt.value);
                }}
                className="sr-only"
              />
              <span className="block font-serif text-base font-bold">{opt.label}</span>
              {opt.hint && (
                <span
                  className={`mt-0.5 block font-serif text-xs italic ${
                    checked ? 'text-stone-200' : 'text-stone-500'
                  }`}
                >
                  {opt.hint}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
